import React, { useState, useEffect } from 'react';
import { Stack, TextField, MenuItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../../app/hooks';
import { 
  useUpsertRuleMutation, 
  useDeleteRuleMutation, 
  useGetMedicareContractsQuery,
  contractApi 
} from '../../../../../services/ContractService';
import { RuleCommands } from './RuleCommands';

interface MedicarePercentByFeeTypeProps {
  ruleId: string;
  ruleString?: string;
  ruleDescription?: string;
}

interface MedicarePercentByFeeTypeRule {
  contract: string;
  category: string;
  rate: number;
}

const MedicarePercentByFeeType: React.FC<MedicarePercentByFeeTypeProps> = ({
  ruleId,
  ruleString,
  ruleDescription,
}) => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const company = searchParams.get('company');
  const dispatch = useAppDispatch();
  const [rule, setRule] = useState<MedicarePercentByFeeTypeRule>({ 
    contract: '', 
    category: '', 
    rate: 0 
  });
  
  const { data: medicareContracts = [] } = useGetMedicareContractsQuery({ company: company || 'Default' });
  const [upsertRule] = useUpsertRuleMutation();
  const [deleteRule] = useDeleteRuleMutation();

  useEffect(() => {
    if (ruleString && ruleString !== '') {
      try {
        const parsedRule: MedicarePercentByFeeTypeRule = JSON.parse(ruleString);
        setRule(parsedRule);
      } catch (error) {
        console.error('Failed to parse rule string:', error);
      }
    }
  }, [ruleString, ruleId]);

  const save = async () => {
    if (!contractId) return;

    try {
      const ruleData = {
        contractId: contractId,
        ruleId: ruleId === '0' ? '' : ruleId,
        ruleType: 'MedicarePercentByFeeType',
        priority: 0,
        ruleString: JSON.stringify(rule),
        description: ruleDescription ?? '',
      };

      await upsertRule(ruleData).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const ruleDelete = async () => {
    if (ruleId === '0') return;

    try {
      await deleteRule({ ruleId }).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  return (
    <React.Fragment>
      <Stack direction="row" gap={1} sx={{ mb: 2 }}>
        <TextField
          label="Contract"
          select
          size="small"
          sx={{ width: '250px' }}
          InputLabelProps={{ shrink: true }}
          value={rule.contract}
          onChange={(e) => setRule({ ...rule, contract: e.target.value })}
        >
          <MenuItem value="">Unknown</MenuItem>
          {medicareContracts.map((contract: any) => (
            <MenuItem key={contract.id} value={contract.id}>
              {contract.payer}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Fee Schedule Category"
          value={rule.category}
          sx={{ width: '250px' }}
          onChange={(e) => setRule({ ...rule, category: e.target.value })}
        />
        <TextField
          type="number"
          label="Rate %"
          value={rule.rate}
          sx={{ width: '100px' }}
          inputProps={{ step: 0.01 }}
          onChange={(e) => setRule({ ...rule, rate: Number(e.target.value) })}
        />
      </Stack>
      <RuleCommands
        saveCommand={save}
        deleteCommand={ruleDelete}
        isNewRule={ruleId === '0'}
      />
    </React.Fragment>
  );
};

export { MedicarePercentByFeeType };